import {MERCADO_ADS_CHANNEL} from '../channels';
import {LocalStorage} from '../../../utils/LocalStorage';

/**
 * Mercado Ads (Product Ads de Mercado Libre) sin React ni red: lo que decide cada pantalla del canal
 * (tanda «Mercado Ads como canal», 20/10/2026, contrato §3 y §6).
 *
 * - **El flujo viaja en el `state`** del OAuth (`ag.` agencia, `pt.` Portal, `lk.` link para el vendedor):
 *   Mercado Libre acepta UNA redirect, `/connect-mercado-ads-account`, y el prefijo sólo le dice al
 *   callback a qué endpoint mandar el código. El backend no confía en él.
 * - **Los errores llegan tipados en `status`** y cada uno tiene su título, su texto y sus acciones
 *   (`copy.md` §4). Nunca se muestra el `error_message` del backend.
 * - **El resultado de una conexión** pasa del callback a la pantalla de destino por `sessionStorage`,
 *   una sola vez: el error se dibuja en la tarjeta de la conexión, no en un toast que se va.
 */

export const MERCADO_ADS_SLUG = 'mercado_ads';
export const MERCADO_ADS_CALLBACK_PATH = '/connect-mercado-ads-account';
export const MERCADO_ADS_AUTH_URL = 'get_auth_url/mercado_ads/';
export const MERCADO_ADS_EXCHANGE_URL = 'get_access_tokens/mercado_ads/';

// Espejo de `max_historical_lookback_days` del backend: Mercado Libre guarda 90 días y se trae desde 88
// atrás, con margen por la zona horaria de cada país.
export const MERCADO_ADS_HISTORY_DAYS = 88;

export const FLOW_AGENCY = 'ag';
export const FLOW_PORTAL = 'pt';
export const FLOW_LINK = 'lk';
const FLOWS = [FLOW_AGENCY, FLOW_PORTAL, FLOW_LINK];

/** El flujo del `state` (`'ag'`, `'pt'` o `'lk'`), o `null` si no viene o no es uno de esos. */
export function flowOf(state) {
    if (typeof state !== 'string') return null;
    const prefix = state.split('.')[0];
    return FLOWS.includes(prefix) ? prefix : null;
}

// --- Países ---------------------------------------------------------------------------------------

// `site_id` → bandera y moneda local (espejo de `mercado_ads/sites.py`). El nombre del país va por i18n.
export const SITES = {
    MLA: {flag: '🇦🇷', currency: 'ARS'},
    MLM: {flag: '🇲🇽', currency: 'MXN'},
    MLB: {flag: '🇧🇷', currency: 'BRL'},
    MLC: {flag: '🇨🇱', currency: 'CLP'},
    MCO: {flag: '🇨🇴', currency: 'COP'},
    MLU: {flag: '🇺🇾', currency: 'UYU'},
    MPE: {flag: '🇵🇪', currency: 'PEN'},
};

const SITE_BY_CURRENCY = Object.keys(SITES).reduce((map, site) => ({...map, [SITES[site].currency]: site}), {});

function normalizeSite(siteId) {
    return siteId ? String(siteId).trim().toUpperCase() : null;
}

/**
 * El país de un anunciante: por `site_id`, o por la moneda si no vino (cada moneda es de un solo país).
 * `null` si no se puede saber.
 */
export function siteOf(advertiser) {
    if (!advertiser) return null;
    const site = normalizeSite(advertiser.site_id);
    if (site && SITES[site]) return site;
    const currency = advertiser.currency || advertiser.currency_code;
    return (currency && SITE_BY_CURRENCY[String(currency).toUpperCase()]) || null;
}

export function flagOf(advertiser) {
    const site = siteOf(advertiser);
    return site ? SITES[site].flag : null;
}

/** «{nombre} · {país} ({moneda})»; sin país conocido, el nombre (con la moneda si vino). */
export function advertiserText(t, advertiser) {
    const name = (advertiser && advertiser.name) || '—';
    const site = siteOf(advertiser);
    const currency = (advertiser && (advertiser.currency || advertiser.currency_code)) || (site && SITES[site].currency);
    if (!site) return currency ? `${name} (${currency})` : name;
    return t('connections.mercado_ads.advertiser_row', {
        name, country: t(`connections.mercado_ads.site.${site}`), currency, interpolation: {escapeValue: false},
    });
}

/** Desde qué día trae historia la conexión: hoy menos `MERCADO_ADS_HISTORY_DAYS`. */
export function historyStartDate(now = new Date()) {
    const date = new Date(now.getTime());
    date.setDate(date.getDate() - MERCADO_ADS_HISTORY_DAYS);
    return date;
}

/** «24 de julio de 2026», en el idioma de la pantalla. */
export function formatHistoryDate(date, language) {
    if (!date || Number.isNaN(new Date(date).getTime())) return null;
    return new Date(date).toLocaleDateString(language || 'es', {day: 'numeric', month: 'long', year: 'numeric'});
}

/** El aviso fijo de los 90 días, con la fecha desde la que se trae (o la que mandó el backend). */
export function historyNoticeText(t, language, from = null) {
    const date = formatHistoryDate(from || historyStartDate(), language);
    return t('connections.mercado_ads.history_notice', {date, interpolation: {escapeValue: false}});
}

function nicknameOfCredential(credential) {
    const nickname = credential && (credential.nickname || credential.platform_user_name);
    return nickname ? String(nickname).trim() || null : null;
}

/**
 * El nombre de una conexión de Mercado Ads. El backend la crea con el id numérico del usuario de Mercado
 * Libre como `label` (así la reconoce al reconectar), que no le dice nada a nadie: mientras siga siendo ése,
 * se muestra el apodo del vendedor. Si la agencia la renombró, su nombre.
 */
export function mercadoAdsConnectionName(credential) {
    if (!credential) return null;
    const label = credential.label ? String(credential.label).trim() : '';
    const nickname = nicknameOfCredential(credential);
    if (nickname && (!label || /^\d+$/.test(label))) return nickname;
    return label || nickname;
}

/**
 * «Vendedor: APODO»: de quién es la cuenta de Mercado Libre que autorizó el acceso. `null` si no se sabe
 * o si el nombre de la conexión ya es el apodo (sería decirlo dos veces).
 */
export function sellerText(t, credential) {
    const nickname = nicknameOfCredential(credential);
    if (!nickname || mercadoAdsConnectionName(credential) === nickname) return null;
    return t('connections.mercado_ads.seller', {nickname, interpolation: {escapeValue: false}});
}

// --- Errores --------------------------------------------------------------------------------------

export const ERROR_NOT_MAIN_USER = 'MERCADO_ADS_NOT_MAIN_USER';
export const ERROR_NO_PRODUCT_ADS = 'MERCADO_ADS_NO_PRODUCT_ADS';
export const ERROR_STATE_INVALID = 'MERCADO_ADS_STATE_INVALID';
export const ERROR_TEMPORARY = 'MERCADO_ADS_TEMPORARY_ERROR';
export const ERROR_NOT_CONFIGURED = 'CONNECTOR_NOT_CONFIGURED';
export const ERROR_NOT_AVAILABLE = 'CONNECTION_NOT_AVAILABLE';
// Cualquier otro código (o un corte de red): un texto propio con reintento.
export const ERROR_GENERIC = 'GENERIC';
export const CANCELLED = 'CANCELLED';

export const ACTION_RETRY = 'retry';
export const ACTION_OTHER_ACCOUNT = 'other_account';
export const ACTION_SEND_LINK = 'send_link';

// Los seis códigos de `contrato.md` §3, con sus acciones (`copy.md` §4). `CONNECTION_NOT_AVAILABLE` no
// distingue quién tiene la cuenta (aislamiento): su texto es el neutro del Portal.
const ERROR_ACTIONS = {
    [ERROR_NOT_MAIN_USER]: [ACTION_OTHER_ACCOUNT, ACTION_SEND_LINK],
    [ERROR_NO_PRODUCT_ADS]: [ACTION_RETRY],
    [ERROR_STATE_INVALID]: [ACTION_RETRY],
    [ERROR_TEMPORARY]: [ACTION_RETRY],
    [ERROR_NOT_CONFIGURED]: [],
    [ERROR_NOT_AVAILABLE]: [],
    [ERROR_GENERIC]: [ACTION_RETRY],
};

export const KNOWN_ERROR_CODES = [
    ERROR_NOT_MAIN_USER, ERROR_NO_PRODUCT_ADS, ERROR_STATE_INVALID, ERROR_TEMPORARY, ERROR_NOT_CONFIGURED,
    ERROR_NOT_AVAILABLE,
];

/**
 * Lo que dice la vuelta de Mercado Libre cuando NO trae código (`?error=…&error_description=…`).
 * Cancelar es sólo `access_denied`; un colaborador (`invalid_operator_user_id`, en el error o en su
 * descripción) es el error del colaborador, no una cancelación; cualquier otro (`invalid_request`,
 * `server_error`, `temporarily_unavailable`…) es el genérico. Sin `error` y sin `code`, también.
 */
export function redirectErrorOf(error, description = null) {
    const text = `${error || ''} ${description || ''}`;
    if (/invalid_operator_user_id/i.test(text)) return ERROR_NOT_MAIN_USER;
    if (error === 'access_denied') return CANCELLED;
    return ERROR_GENERIC;
}

/** El código de una respuesta que no es OK: uno de los seis, o `GENERIC`. */
export function errorCodeOf(response) {
    const code = response && (response.status || response.error_code);
    return KNOWN_ERROR_CODES.includes(code) ? code : ERROR_GENERIC;
}

/**
 * El apodo del vendedor que viaja como extra de un error (`nickname`): en el cuerpo, en `data` o en
 * `errors`, que el backend a veces manda como string JSON.
 */
export function nicknameOf(response) {
    if (!response) return null;
    const candidates = [response.nickname, response.data && response.data.nickname];
    const raw = response.raw_body;
    if (raw) candidates.push(raw.nickname, raw.data && raw.data.nickname);
    const errors = response.errors !== undefined ? response.errors : raw && raw.errors;
    if (errors) {
        try {
            const extra = typeof errors === 'string' ? JSON.parse(errors) : errors;
            if (extra && !Array.isArray(extra)) candidates.push(extra.nickname);
        } catch (e) {
            // `errors` sin JSON: no trae apodo.
        }
    }
    const nickname = candidates.find((value) => typeof value === 'string' && value.trim());
    return nickname ? nickname.trim() : null;
}

/** Título, texto y acciones de un error de la conexión (las claves de `copy.md` §4). */
export function errorViewOf(code, nickname = null) {
    const known = ERROR_ACTIONS[code] ? code : ERROR_GENERIC;
    const base = `connections.mercado_ads.error.${known}`;
    if (known === ERROR_NOT_AVAILABLE) {
        return {code: known, titleKey: null, textKey: 'portal_connections.connect_error', actions: []};
    }
    if (known === ERROR_NO_PRODUCT_ADS && !nickname) {
        return {code: known, titleKey: `${base}.title_unnamed`, textKey: `${base}.text`, actions: ERROR_ACTIONS[known]};
    }
    return {code: known, titleKey: `${base}.title`, textKey: `${base}.text`, actions: ERROR_ACTIONS[known], nickname};
}

// --- El resultado de una conexión, del callback a la pantalla de destino ----------------------------

// Las borra también `LocalStorage.clearSessionData()` (cerrar sesión o entrar con otra cuenta).
export const RESULT_KEY = 'dashbo.mercado_ads.connect_result';
// Lo que tarda en montar la pantalla de destino, con margen para un login de por medio.
const RESULT_MAX_AGE_MS = 10 * 60 * 1000;

function storage() {
    try {
        return window.sessionStorage;
    } catch (e) {
        return null;
    }
}

// De quién es el resultado: la sesión que canjeó. El apodo y los anunciantes son de esa agencia, y la
// pestaña puede quedar abierta para otra persona (cierra sesión, entra otra cuenta).
function sessionOwner() {
    return {agencyId: LocalStorage.fetchAgencyId() || null, userId: LocalStorage.fetchUserId() || null};
}

/**
 * Deja el resultado para la pantalla a la que vuelve el callback. `flow` dice quién lo lee (agencia o
 * Portal): una pantalla nunca consume el resultado de la otra, y una sesión nunca el de otra sesión.
 */
export function saveConnectResult(flow, result) {
    const box = storage();
    if (!box) return;
    try {
        box.setItem(RESULT_KEY, JSON.stringify({...result, flow, owner: sessionOwner(), at: Date.now()}));
    } catch (e) {
        // Sin almacenamiento la pantalla vuelve sin la tarjeta: el toast ya avisó.
    }
}

/**
 * El resultado que dejó el callback para este flujo y para ESTA sesión (agencia y usuario), una sola vez.
 * `null` si no hay, venció o es de otra sesión (que se descarta).
 */
export function consumeConnectResult(flow, now = Date.now()) {
    const box = storage();
    if (!box) return null;
    try {
        const parsed = JSON.parse(box.getItem(RESULT_KEY));
        if (!parsed) return null;
        const owner = sessionOwner();
        const sameSession = parsed.owner && String(parsed.owner.agencyId) === String(owner.agencyId)
            && String(parsed.owner.userId) === String(owner.userId);
        if (!sameSession) {
            box.removeItem(RESULT_KEY);
            return null;
        }
        if (parsed.flow !== flow) return null;
        box.removeItem(RESULT_KEY);
        if (typeof parsed.at !== 'number' || now - parsed.at > RESULT_MAX_AGE_MS) return null;
        return parsed;
    } catch (e) {
        return null;
    }
}

/** Lo que el callback guarda de un canje bien hecho (`contrato.md` §3). */
export function successResultOf(response) {
    const data = (response && response.data) || {};
    return {
        status: 'OK',
        channel: MERCADO_ADS_CHANNEL,
        credentialId: data.credential_id || null,
        nickname: data.nickname || data.label || null,
        relinked: data.relinked === true,
        advertisers: Array.isArray(data.advertisers) ? data.advertisers.filter(Boolean) : [],
        linkedAdvertiserIds: Array.isArray(data.linked_advertiser_ids) ? data.linked_advertiser_ids : [],
        needsSelection: data.needs_selection === true,
    };
}

/** Lo que el callback guarda de un canje que falló: el código y, si vino, el apodo. */
export function errorResultOf(response) {
    return {status: errorCodeOf(response), nickname: nicknameOf(response)};
}

/**
 * La URL de autorización que manda el backend. La forma es la de `get_auth_url/dv360/` (`auth_url`
 * pelado), pero también se acepta envuelta en `data` o como `url`: el endpoint es nuevo.
 */
export function authUrlOf(response) {
    if (!response) return null;
    const data = response.data || {};
    return response.auth_url || data.auth_url || data.url || response.url || null;
}
